import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Rates() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [manual, setManual] = useState({ gold_24k: "", silver: "" });
  const [multipliers, setMultipliers] = useState({});
  const [previewIn, setPreviewIn] = useState({ gold_24k: "", silver: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/admin/rates").then((r) => {
      setData(r.data);
      setManual({ gold_24k: r.data.state.gold_24k, silver: r.data.state.silver });
      setMultipliers(r.data.purity_multipliers || {});
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/rates/history", { params: { limit: 30 } }).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (!data) return <p className="text-neutral-400 font-mono text-sm">LOADING…</p>;

  const act = async (fn, key, msg) => {
    setBusy(key);
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const runPreview = () => act(async () => {
    const r = await api.post("/admin/rates/preview", {
      gold_24k: previewIn.gold_24k ? Number(previewIn.gold_24k) : null,
      silver: previewIn.silver ? Number(previewIn.silver) : null,
    });
    setPreview(r.data);
  }, "preview");

  const derived = (label, base, mult) => (
    <div key={label} className="flex justify-between text-sm border-b border-neutral-100 py-1.5">
      <span className="text-neutral-600">{label}</span>
      <span className="font-mono font-medium">{inr(Math.round(base * mult * 100) / 100)}/g</span>
    </div>
  );

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-rates-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Metal Rates</h1>

      {data.stale && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 text-sm" data-testid="stale-warning">
          <AlertTriangle size={18} className="shrink-0" />
          Rates are stale — last updated {fmtDateTime(data.state.updated_at)} (older than {data.stale_hours}h). Update before customers check out.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-neutral-200 p-5" data-testid="rate-gold-card">
          <p className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">GOLD 24K / GRAM</p>
          <p className="font-heading text-2xl font-bold mt-2">{inr(data.state.gold_24k)}</p>
        </div>
        <div className="bg-white border border-neutral-200 p-5" data-testid="rate-silver-card">
          <p className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">SILVER (PURE) / GRAM</p>
          <p className="font-heading text-2xl font-bold mt-2">{inr(data.state.silver)}</p>
        </div>
        <div className="bg-white border border-neutral-200 p-5">
          <p className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">SOURCE</p>
          <p className="font-mono font-semibold mt-2 uppercase" data-testid="rate-source">{data.state.source}</p>
        </div>
        <div className="bg-white border border-neutral-200 p-5">
          <p className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">LAST UPDATED</p>
          <p className="text-sm font-medium mt-2" data-testid="rate-updated-at">{fmtDateTime(data.state.updated_at)}</p>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 p-5">
        <h2 className="font-heading font-semibold mb-2">Derived purity rates</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
          <div>
            {derived("22K Gold", data.state.gold_24k, multipliers["22K"] ?? 0.916)}
            {derived("18K Gold", data.state.gold_24k, multipliers["18K"] ?? 0.75)}
            {derived("14K Gold", data.state.gold_24k, multipliers["14K"] ?? 0.585)}
          </div>
          <div>{derived("925 Silver", data.state.silver, multipliers["925"] ?? 0.925)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white border border-neutral-200 p-5">
          <h2 className="font-heading font-semibold mb-3">Manual override</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Gold 24K ₹/g</Label><Input type="number" step="0.01" value={manual.gold_24k} onChange={(e) => setManual({ ...manual, gold_24k: e.target.value })} data-testid="manual-gold-input" /></div>
            <div><Label>Silver ₹/g</Label><Input type="number" step="0.01" value={manual.silver} onChange={(e) => setManual({ ...manual, silver: e.target.value })} data-testid="manual-silver-input" /></div>
          </div>
          <Button className="mt-3 bg-stone-900 hover:bg-brand text-white btn-press" disabled={!!busy}
            onClick={() => act(() => api.post("/admin/rates/manual", { gold_24k: Number(manual.gold_24k), silver: Number(manual.silver) }), "manual", "Rate updated — all prices recomputed")}
            data-testid="apply-manual-rate-btn">
            Apply Rate
          </Button>
        </section>

        <section className="bg-white border border-neutral-200 p-5">
          <h2 className="font-heading font-semibold mb-3">Rate source</h2>
          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <Label>Source</Label>
              <Select value={data.rate_source} onValueChange={(v) => setData({ ...data, rate_source: v })}>
                <SelectTrigger data-testid="rate-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="api">API (polled)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Poll every (min)</Label><Input type="number" value={data.poll_interval_minutes} onChange={(e) => setData({ ...data, poll_interval_minutes: Number(e.target.value) })} data-testid="poll-interval-input" /></div>
            <div><Label>Stale after (hrs)</Label><Input type="number" value={data.stale_hours} onChange={(e) => setData({ ...data, stale_hours: Number(e.target.value) })} data-testid="stale-hours-input" /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" disabled={!!busy}
              onClick={() => act(() => api.put("/admin/rates/source", { source: data.rate_source, poll_interval_minutes: data.poll_interval_minutes, stale_hours: data.stale_hours }), "source", "Rate source saved")}
              data-testid="save-rate-source-btn">Save Source</Button>
            <Button variant="outline" disabled={!!busy}
              onClick={() => act(() => api.post("/admin/rates/poll-now"), "poll", "Fetched latest rate")}
              data-testid="poll-now-btn">
              <RefreshCw size={14} className="mr-1" /> Poll Now ({data.adapter}{data.api_configured ? "" : " — no API URL set"})
            </Button>
          </div>
        </section>
      </div>

      <section className="bg-white border border-neutral-200 p-5">
        <h2 className="font-heading font-semibold mb-3">Purity multipliers (of base rate)</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {["24K", "22K", "18K", "14K", "925"].map((k) => (
            <div key={k}>
              <Label>{k}</Label>
              <Input type="number" step="0.001" min="0.01" max="1" value={multipliers[k] ?? ""} onChange={(e) => setMultipliers({ ...multipliers, [k]: Number(e.target.value) })} data-testid={`multiplier-${k}`} />
            </div>
          ))}
        </div>
        <Button variant="outline" className="mt-3" disabled={!!busy}
          onClick={() => act(() => api.put("/admin/rates/multipliers", { multipliers }), "mult", "Multipliers saved")}
          data-testid="save-multipliers-btn">Save Multipliers</Button>
      </section>

      <section className="bg-white border border-neutral-200 p-5" data-testid="recalc-preview">
        <h2 className="font-heading font-semibold mb-1">Bulk recalculation preview</h2>
        <p className="text-xs text-neutral-500 mb-3">See every SKU's price at a proposed rate before applying it.</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div><Label>Proposed gold 24K ₹/g</Label><Input type="number" placeholder={data.state.gold_24k} value={previewIn.gold_24k} onChange={(e) => setPreviewIn({ ...previewIn, gold_24k: e.target.value })} className="w-44" data-testid="preview-gold-input" /></div>
          <div><Label>Proposed silver ₹/g</Label><Input type="number" placeholder={data.state.silver} value={previewIn.silver} onChange={(e) => setPreviewIn({ ...previewIn, silver: e.target.value })} className="w-40" data-testid="preview-silver-input" /></div>
          <Button variant="outline" onClick={runPreview} disabled={!!busy} data-testid="run-preview-btn">Preview</Button>
          {preview && (
            <Button className="bg-stone-900 hover:bg-brand text-white btn-press" disabled={!!busy}
              onClick={() => act(() => api.post("/admin/rates/manual", { gold_24k: preview.proposed.gold_24k, silver: preview.proposed.silver }), "apply", "Proposed rate applied")}
              data-testid="apply-preview-btn">Apply This Rate</Button>
          )}
        </div>
        {preview && (
          <div className="overflow-x-auto mt-4 max-h-80 overflow-y-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                  <th className="py-2">SKU</th><th>Product</th><th className="text-right">Current</th><th className="text-right">Proposed</th><th className="text-right pr-2">Δ</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.sku} className="border-b border-neutral-100" data-testid={`preview-row-${r.sku}`}>
                    <td className="py-1.5 font-mono text-xs">{r.sku}</td>
                    <td className="text-xs">{r.product} <span className="text-neutral-400">({r.purity} {r.metal})</span></td>
                    <td className="text-right font-mono">{inr(r.current)}</td>
                    <td className="text-right font-mono">{inr(r.proposed)}</td>
                    <td className={`text-right font-mono pr-2 ${r.delta > 0 ? "text-emerald-700" : r.delta < 0 ? "text-red-600" : ""}`}>
                      {r.delta > 0 ? "+" : ""}{inr(r.delta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-neutral-200 p-5" data-testid="rate-history">
        <h2 className="font-heading font-semibold mb-3">Rate history</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                <th className="py-2">When</th><th>Gold 24K</th><th>Silver</th><th>Source</th><th>Changed by</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b border-neutral-100" data-testid={`rate-history-${h.id}`}>
                  <td className="py-2">{fmtDateTime(h.created_at)}</td>
                  <td className="font-mono">{inr(h.gold_24k)}/g</td>
                  <td className="font-mono">{inr(h.silver)}/g</td>
                  <td className="font-mono text-xs uppercase">{h.source}</td>
                  <td className="text-xs text-neutral-500">{h.changed_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
